import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
} from '@mui/material';
import { Delete, Visibility, ArrowBack, Description, Map, TrendingUp, CalendarToday, BarChart } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import predictionComparisonService, { PredictionComparison } from '../services/predictionComparisonService';
import { format } from 'date-fns';

export const PredictionComparisonsPage: React.FC = () => {
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState<PredictionComparison[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadComparisons();
  }, []);

  const loadComparisons = async () => {
    try {
      setLoading(true);
      const startTime = performance.now();
      const data = await predictionComparisonService.getComparisons();
      const endTime = performance.now();
      console.log(`API call took ${(endTime - startTime).toFixed(0)}ms`);
      console.log(`Loaded ${data.length} comparisons`);
      setComparisons(data);
    } catch (err: any) {
      console.error('Failed to load comparisons:', err);
      setSnackbar({
        open: true,
        message: 'Nem sikerült betölteni az összehasonlításokat: ' + (err.response?.data?.error || err.message),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Biztosan törölni szeretnéd ezt az összehasonlítást?')) {
      return;
    }

    try {
      await predictionComparisonService.deleteComparison(id);
      setSnackbar({ open: true, message: 'Összehasonlítás törölve', severity: 'success' });
      loadComparisons();
    } catch (err: any) {
      console.error('Failed to delete comparison:', err);
      setSnackbar({
        open: true,
        message: 'Törlés sikertelen: ' + (err.response?.data?.error || err.message),
        severity: 'error',
      });
    }
  };

  const handleView = (comparison: PredictionComparison) => {
    // Navigate to prediction detail with comparison params
    navigate(`/predictions/${comparison.predictionId}?comparisonId=${comparison.id}`);
  };

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/pricings')}>
            Vissza
          </Button>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChart /> Terv/Tényadat Összehasonlítások
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : comparisons.length === 0 ? (
            <Alert severity="info">
              Még nincs mentett összehasonlítás. Hozz létre egyet egy előrejelzés oldalán!
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {comparisons.map((comp) => (
                <Grid item xs={12} md={6} lg={4} key={comp.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header: Name and Date */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 600, flexGrow: 1, mr: 1 }}>
                          {comp.name}
                        </Typography>
                        <Chip
                          label={comp.yearMonth}
                          size="small"
                          color="primary"
                          icon={<CalendarToday />}
                        />
                      </Box>

                      {comp.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {comp.description}
                        </Typography>
                      )}

                      <Divider sx={{ my: 2 }} />

                      {/* Prediction Details */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                          Előrejelzés adatai
                        </Typography>

                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description fontSize="small" color="action" />
                          <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                            {comp.prediction.fileName}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Map fontSize="small" color="action" />
                          <Typography variant="body2">
                            {comp.prediction.mappingName}
                          </Typography>
                        </Box>

                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUp fontSize="small" color="action" />
                          <Chip
                            label={comp.prediction.modelType}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Fact Data Details */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                          Tényadatok
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {comp.creditTypeNames}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Ügydarab</Typography>
                            <Typography variant="body1" fontWeight="600">
                              {comp.actualCaseCount} db
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Normalizálás</Typography>
                            <Typography variant="body1" fontWeight="600">
                              ×{comp.normalizationFactor.toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Induló tartozás</Typography>
                            <Typography variant="body1" fontWeight="600">
                              {Math.round(comp.actualInitialDebt || 0).toLocaleString('hu-HU')} Ft
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Mentve: {format(new Date(comp.createdAt), 'yyyy-MM-dd HH:mm')}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<Visibility />}
                        onClick={() => handleView(comp)}
                      >
                        Megtekintés
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(comp.id)}
                        title="Törlés"
                      >
                        <Delete />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
